import BackLinkButton from '@/components/atoms/BackLinkButton';
import Button from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import useGetPromotionSetting from '@/hooks/api/useGetPromotionSetting';
import promotionState, { initialPromotionState } from '@/states/promotionState';
import { Promotion } from '@/types/api/mypage/promotionInfo';
import { Constants } from '@/utils/constants';
import Router from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

/**
 * プロモーション情報配信設定入力画面。
 * 各種案内メール/SMS の受取設定を変更し、確認画面へ渡すための state を更新する。
 */
const PromotionChange = () => {
  // 固有State
  const [userPromotionInfo, setPromotionInfo] = useRecoilState(promotionState);
  const initialPromotionInfo = useRecoilValue(initialPromotionState);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // API呼び出しhooks
  const { execute: getPromotionSetting } = useGetPromotionSetting();

  // 初回表示時のみ現在設定を取得し、入力 state を初期化する
  useEffect((): void => {
    if (userPromotionInfo.length > 0 && userPromotionInfo !== initialPromotionInfo) return;

    getPromotionSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** チェックボックス変更を Recoil state に反映する */
  const setCheckBoxVal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPromotionSettings: Promotion[] = userPromotionInfo.map((item) => {
      if (item.type === e.target.value) {
        return { ...item, is_enabled: !item.is_enabled };
      }
      return item;
    });

    setPromotionInfo(newPromotionSettings);
  };

  /**
   * 変更がある場合のみ確認画面へ進める。
   * 未変更のまま送信させないことで、無意味な更新 API を防ぐ。
   */
  const handleClickConfirmButton = () => {
    if (JSON.stringify(userPromotionInfo) === JSON.stringify(initialPromotionInfo)) {
      setShowErrorMessage(true);
      return;
    }

    Router.push('/contract/promotion/confirm');
  };

  /** 指定した配信種別が有効化されているかを判定する */
  const isChecked = (value: string) => {
    return userPromotionInfo.find((item) => item.type === value)?.is_enabled;
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>プロモーション情報配信設定</Heading>

        {showErrorMessage && (
          <strong className='m-text' data-testid='error-message' role='alert'>
            メール配信等の受取設定が変更されていません。メール配信等の受取設定を変更してください。
          </strong>
        )}

        <div className='m-box-02' id=''>
          <div className='m-box-02__inner m-text'>
            <p>プロモーション設定を変更します。ご希望のプロモーション設定をご入力ください。</p>
            <br />
            <div className='align-center' style={{ marginBottom: '32px' }}>
              <h2
                className='m-heading-l2__hdg align-center'
                style={{ textAlign: 'center', width: '100%', display: 'block' }}
              >
                サービスに関するご案内
              </h2>
              <p style={{ marginBottom: '24px' }}>[新サービス、新端末、キャンペーン等のご案内]</p>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 0 0',
                  display: 'inline-block',
                }}
              >
                <li style={{ display: 'inline-block', marginRight: '16px' }}>
                  <input
                    type='checkbox'
                    id='service-mail'
                    name='service-mail'
                    data-testid='service-mail'
                    value={Constants.Promotion.Type.UP_SELL_MAIL}
                    checked={isChecked(Constants.Promotion.Type.UP_SELL_MAIL)}
                    onChange={setCheckBoxVal}
                  />
                  <label htmlFor='service-mail' className='mgr-10px'>
                    メールを受け取る
                  </label>
                </li>
                <li style={{ display: 'inline-block' }}>
                  <input
                    type='checkbox'
                    id='service-sms'
                    name='service-sms'
                    data-testid='service-sms'
                    value={Constants.Promotion.Type.UP_SELL_SMS}
                    checked={isChecked(Constants.Promotion.Type.UP_SELL_SMS)}
                    onChange={setCheckBoxVal}
                  />
                  <label htmlFor='service-sms'>SMSを受け取る</label>
                </li>
              </ul>
            </div>
            <br />
            <div className='align-center' style={{ marginBottom: '32px' }}>
              <h2
                className='m-heading-l2__hdg align-center'
                style={{ textAlign: 'center', width: '100%', display: 'block' }}
              >
                優待特典に関するご案内
              </h2>
              <p style={{ marginBottom: '24px' }}>
                [NURO会員限定の特典(ソニーストアお買い物券、電子雑誌等)]
              </p>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 0 0',
                  display: 'inline-block',
                }}
              >
                <li style={{ display: 'inline-block', marginRight: '16px' }}>
                  <input
                    type='checkbox'
                    id='privilege-mail'
                    name='privilege-mail'
                    data-testid='privilege-mail'
                    value={Constants.Promotion.Type.BENEFIT_MAIL}
                    checked={isChecked(Constants.Promotion.Type.BENEFIT_MAIL)}
                    onChange={setCheckBoxVal}
                  />
                  <label htmlFor='privilege-mail' className='mgr-10px'>
                    メールを受け取る
                  </label>
                </li>
                <li style={{ display: 'inline-block' }}>
                  <input
                    type='checkbox'
                    id='privilege-sms'
                    name='privilege-sms'
                    data-testid='privilege-sms'
                    value={Constants.Promotion.Type.BENEFIT_SMS}
                    checked={isChecked(Constants.Promotion.Type.BENEFIT_SMS)}
                    onChange={setCheckBoxVal}
                  />
                  <label htmlFor='privilege-sms'>SMSを受け取る</label>
                </li>
              </ul>
            </div>
            <br />
            <div className='align-center' style={{ marginBottom: '32px' }}>
              <h2
                className='m-heading-l2__hdg align-center'
                style={{
                  textAlign: 'center',
                  width: '100%',
                  display: 'block',
                  marginBottom: '24px',
                }}
              >
                自社および提携会社の製品・サービスに関するご案内
              </h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'inline-block' }}>
                <li style={{ display: 'inline-block', marginRight: '16px' }}>
                  <input
                    type='checkbox'
                    id='product-mail'
                    name='product-mail'
                    data-testid='product-mail'
                    value={Constants.Promotion.Type.CROSS_SELL_MAIL}
                    checked={isChecked(Constants.Promotion.Type.CROSS_SELL_MAIL)}
                    onChange={setCheckBoxVal}
                  />
                  <label htmlFor='product-mail' className='mgr-10px'>
                    メールを受け取る
                  </label>
                </li>
                <li style={{ display: 'inline-block' }}>
                  <input
                    type='checkbox'
                    id='product-sms'
                    name='product-sms'
                    data-testid='product-sms'
                    value={Constants.Promotion.Type.CROSS_SELL_SMS}
                    checked={isChecked(Constants.Promotion.Type.CROSS_SELL_SMS)}
                    onChange={setCheckBoxVal}
                  />
                  <label htmlFor='product-sms'>SMSを受け取る</label>
                </li>
              </ul>
            </div>
            <br />
            {/* ボタン配置-横並び */}
            <div className='m-button col2'>
              <BackLinkButton href='/contract'>メニューへ戻る</BackLinkButton>
              <Button variant='secondary' onClick={handleClickConfirmButton}>
                入力内容を確認する
              </Button>
              {/* <LinkButton variant='secondary' href='/contract/promotion/confirm'>
              入力内容を確認する
            </LinkButton> */}
            </div>
          </div>
        </div>
        <BreadcrumbLists />
      </main>
    </>
  );
};

export default PromotionChange;
